/**
 * @file time-picker/time-picker.tokens.ts
 *
 * MD3 Design tokens for Time Picker component system.
 * Verified mapping from:
 *   - TimePickerTokens.kt → ClockDial, TimeSelector, PeriodSelector (Picker mode)
 *   - TimeInputTokens.kt  → TimeField, PeriodSelector (Input mode)
 *
 * CSS variables → packages/tokens/src/colors.css
 * Tailwind classes → packages/tailwind/src/index.css
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePickerTokens.kt
 * @see docs/m3/date-and-time-pickers/time-pickers/TimeInputTokens.kt
 */

// ─── Color Tokens ──────────────────────────────────────────────────────────────

/**
 * Color tokens for Time Picker — mapped from TimePickerTokens.kt + TimeInputTokens.kt.
 * Uses CSS custom properties from MD3 theme (packages/tokens/src/colors.css).
 */
export const TP_COLORS = {
	// ── Clock Dial (TimePickerTokens) ─────────────────────────────────────────
	/** Dial background — SurfaceContainerHighest */
	clockDial: "var(--md-sys-color-surface-container-highest)",
	/** Selected number text — OnPrimary */
	clockDialSelected: "var(--md-sys-color-on-primary)",
	/** Unselected number text — OnSurface */
	clockDialUnselected: "var(--md-sys-color-on-surface)",
	/** Selector handle circle — Primary */
	selectorHandle: "var(--md-sys-color-primary)",
	/** Selector track line — Primary */
	selectorTrack: "var(--md-sys-color-primary)",
	/** Selector center dot — Primary */
	selectorCenter: "var(--md-sys-color-primary)",

	// ── Container ─────────────────────────────────────────────────────────────
	/** Picker/Input container — SurfaceContainerHigh */
	container: "var(--md-sys-color-surface-container-high)",

	// ── Time Selector Buttons (hour/minute display) ───────────────────────────
	/** Selected selector (hour/min button) — PrimaryContainer */
	timeSelectorSelected: "var(--md-sys-color-primary-container)",
	/** Unselected selector — SurfaceContainerHighest */
	timeSelectorUnselected: "var(--md-sys-color-surface-container-highest)",
	/** Selected selector text — OnPrimaryContainer */
	timeSelectorSelectedText: "var(--md-sys-color-on-primary-container)",
	/** Unselected selector text — OnSurface */
	timeSelectorUnselectedText: "var(--md-sys-color-on-surface)",

	// ── Period Selector AM/PM (updated flag = true → PrimaryContainer) ────────
	/** AM/PM selected background — TertiaryContainer */
	periodSelected: "var(--md-sys-color-tertiary-container)",
	/** AM/PM unselected background — transparent */
	periodUnselected: "transparent",
	/** AM/PM selected text — OnTertiaryContainer */
	periodSelectedText: "var(--md-sys-color-on-tertiary-container)",
	/** AM/PM unselected text — OnSurfaceVariant */
	periodUnselectedText: "var(--md-sys-color-on-surface-variant)",
	/** AM/PM border — Outline */
	periodOutline: "var(--md-sys-color-outline)",

	// ── Time Field (TimeInput mode, TimeInputTokens) ───────────────────────────
	/** Time field default background — SurfaceContainerHighest */
	timeField: "var(--md-sys-color-surface-container-highest)",
	/** Time field focused background — PrimaryContainer */
	timeFieldFocus: "var(--md-sys-color-primary-container)",
	/** Time field focused text — OnPrimaryContainer */
	timeFieldFocusText: "var(--md-sys-color-on-primary-container)",
	/** Time field focus outline — Primary */
	timeFieldFocusOutline: "var(--md-sys-color-primary)",
	/** Time field default text — OnSurface */
	timeFieldText: "var(--md-sys-color-on-surface)",
	/** ":" separator — OnSurface */
	separator: "var(--md-sys-color-on-surface)",
	/** Headline label ("Select time") — OnSurfaceVariant */
	headline: "var(--md-sys-color-on-surface-variant)",
} as const;

// ─── Tailwind Class Helpers ────────────────────────────────────────────────────

/**
 * Tailwind utility class strings for Time Picker elements.
 * Used with `cn()` function.
 * Mapped from packages/tailwind/src/index.css (--color-m3-* tokens).
 */
export const TP_CLASSES = {
	// ── Container ─────────────────────────────────────────────────────────────
	/** Main picker/input container */
	container: "bg-m3-surface-container-high rounded-m3-xl select-none",

	// ── Time Selector Buttons ─────────────────────────────────────────────────
	/** Hour/minute selector button — selected */
	timeSelectorSelected: "bg-m3-primary-container text-m3-on-primary-container",
	/** Hour/minute selector button — unselected */
	timeSelectorUnselected: "bg-m3-surface-container-highest text-m3-on-surface",

	// ── Period Selector ───────────────────────────────────────────────────────
	/** AM/PM button — selected */
	periodSelected: "bg-m3-primary-container text-m3-on-primary-container",
	/** AM/PM button — unselected */
	periodUnselected: "bg-transparent text-m3-on-surface-variant",

	// ── Time Field (input mode) ────────────────────────────────────────────────
	/** Time input field — default */
	timeField: "bg-m3-surface-container-highest text-m3-on-surface",
	/** Time input field — focused */
	timeFieldFocused:
		"bg-m3-primary-container text-m3-on-primary-container ring-2 ring-m3-primary",

	// ── Clock Dial ─────────────────────────────────────────────────────────────
	/** Clock dial background circle */
	clockDial: "fill-[var(--md-sys-color-surface-container-highest)]",
	/** Selected number on dial */
	clockDialSelectedText: "fill-[var(--md-sys-color-on-primary)]",
	/** Unselected number on dial */
	clockDialUnselectedText: "fill-[var(--md-sys-color-on-surface)]",
	/** Selector handle, track, center dot */
	selectorPrimary: "fill-m3-primary stroke-m3-primary",
} as const;

// ─── Shape Tokens ──────────────────────────────────────────────────────────────

/**
 * Shape tokens — corner radii from MD3 shape scale.
 * Mapped from ShapeKeyTokens in TimePickerTokens.kt + TimeInputTokens.kt.
 *
 * Uses Tailwind class names from packages/tailwind/src/index.css.
 */
export const TP_SHAPE = {
	/** Dialog container — CornerExtraLarge = 28px */
	container: "rounded-m3-xl",
	/** Hour/minute selector button — CornerSmall = 8px */
	timeSelector: "rounded-m3-sm",
	/** Period (AM/PM) container — CornerSmall = 8px */
	periodSelector: "rounded-m3-sm",
	/** Clock dial circle — CornerFull */
	clockDial: "rounded-m3-full",
	/** Time field — CornerSmall = 8px */
	timeField: "rounded-m3-sm",
} as const;

// ─── Dimension Tokens ──────────────────────────────────────────────────────────

/**
 * Fixed dimension tokens from TimePickerTokens.kt + TimeInputTokens.kt.
 * Unit: px (converted from dp).
 */
export const TP_SIZE = {
	// ── Clock Dial (TimePickerTokens) ─────────────────────────────────────────
	/** ClockDialContainerSize = 256dp */
	clockDialSize: 256,
	/** ClockDialSelectorHandleContainerSize = 48dp */
	selectorHandleSize: 48,
	/** ClockDialSelectorCenterContainerSize = 8dp */
	selectorCenterSize: 8,
	/** ClockDialSelectorTrackContainerWidth = 2dp */
	selectorTrackWidth: 2,

	// ── Time Selector (TimeSelectorContainer) ─────────────────────────────────
	/** TimeSelectorContainerWidth = 96dp */
	timeSelectorWidth: 96,
	/** TimeSelectorContainerHeight = 80dp */
	timeSelectorHeight: 80,
	/** TimeSelector24HVerticalContainerWidth = 114dp (24h mode) */
	timeSelector24hWidth: 114,

	// ── Period Selector (Picker mode) ─────────────────────────────────────────
	/** PeriodSelectorVerticalContainerWidth = 52dp */
	periodSelectorVerticalWidth: 52,
	/** PeriodSelectorVerticalContainerHeight = 80dp */
	periodSelectorVerticalHeight: 80,
	/** PeriodSelectorHorizontalContainerWidth = 216dp */
	periodSelectorHorizWidth: 216,
	/** PeriodSelectorHorizontalContainerHeight = 38dp */
	periodSelectorHorizHeight: 38,

	// ── Time Field (Input mode, TimeInputTokens) ───────────────────────────────
	/** TimeFieldContainerWidth = 96dp */
	timeFieldWidth: 96,
	/** TimeFieldContainerHeight = 72dp */
	timeFieldHeight: 72,
	/** PeriodSelectorContainerHeight (Input mode) = 72dp */
	periodInputHeight: 72,
	/** PeriodSelectorContainerWidth (Input mode) = 52dp */
	periodInputWidth: 52,
} as const;
